import { getCurrentInstance, inject, watch } from "vue";
import tinyEmitter from "tiny-emitter";
import compact from "lodash/compact";
import sortBy from "lodash/sortBy";
import pick from "lodash/pick";
import camelCase from "lodash/camelCase";
import mergeWith from "lodash/mergeWith";
import picomatch from "picomatch-browser";
import moment from "moment";
import "moment-precise-range-plugin";

export function convertLinesArrToHtml(linesArr) {
  return compact(linesArr)
    .map((line) => line.trim().replace(/\s+/g, " "))
    .join("<br>");
}

export function sortByAnotherArr(arr, anotherArr) {
  return sortBy(arr, (el) => anotherArr.indexOf(el));
}

export function pickInObjectOrder(obj, keys) {
  const objKeys = Object.keys(obj);

  return pick(obj, sortByAnotherArr(keys, objKeys));
}

export function mergeWithArr(object, ...sources) {
  return mergeWith(object, ...sources, (objValue, srcValue) =>
    objValue instanceof Array ? objValue.concat(srcValue) : undefined
  );
}

export function stripHtml(html) {
  const replaceCommonCharsConfig = {
    [String.fromCharCode(160)]: String.fromCharCode(32),
  };

  const replaceCommonChars = (str) =>
    Object.entries(replaceCommonCharsConfig).reduce(
      (curStr, [searchChar, replaceChar]) =>
        curStr.replaceAll(searchChar, replaceChar),
      str
    );

  let tmp = document.createElement("DIV");
  tmp.innerHTML = html;

  return replaceCommonChars(tmp.textContent || tmp.innerText || "");
}

export function windowReload(type) {
  switch (type) {
    case "root":
      window.location.replace(
        window.location.origin + this.router.options.history.base
      );
      break;
    case undefined:
    case null:
      window.location.reload();
      break;
  }
}

export function injectWithSelf(injectArgs, app) {
  injectArgs = injectArgs instanceof Array ? injectArgs : [injectArgs];
  const injectionKey = injectArgs[0];
  const vm = app?._context || getCurrentInstance();
  return vm?.provides[injectionKey] || inject(...injectArgs);
}

export function createEventBus() {
  const emitter = new tinyEmitter();

  return {
    $on: (...args) => emitter.on(...args),
    $once: (...args) => emitter.once(...args),
    $off: (...args) => emitter.off(...args),
    $emit: (...args) => emitter.emit(...args),
  };
}

export function getSourceFilePathObj(str) {
  let strParts = str.split(":");

  if (strParts.length == 1) strParts = ["", ...strParts];

  strParts[0] = strParts[0] || "app";

  if (strParts[0] != "app" && strParts[0] != "shared-lib")
    throw Error(
      "Invalid source passed, source must be one of 'app' and 'shared-lib'"
    );

  return {
    source: strParts[0],
    filePath: strParts[1],
  };
}

export function getPreventEventEmitObj() {
  let doPreventEventEmit = false;

  const preventEventEmit = () => (doPreventEventEmit = true);
  const isEventEmitPrevented = () => doPreventEventEmit;

  const consumerMethodArgsObj = {
    preventEventEmit,
    isEventEmitPrevented,
  };

  return {
    consumerMethodArgsObj,
    preventEventEmit,
    isEventEmitPrevented,
  };
}

export function matcher(patterns, options) {
  if (patterns instanceof RegExp) {
    return function (str) {
      return patterns.test(str);
    };
  } else {
    return picomatch(patterns, options);
  }
}

export function interval(callback, intervalTime, take) {
  let clearIt = setInterval(callback, intervalTime);
  setTimeout(() => clearInterval(clearIt), intervalTime * take);
}

export function wait(timeout) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

export function syncData(data1, data2, options) {
  watch(
    data1,
    (curVal) => {
      if (curVal !== data2.value) data2.value = curVal;
    },
    options
  );
  watch(
    data2,
    (curVal) => {
      if (curVal !== data1.value) data1.value = curVal;
    },
    options
  );
}

export function getAppMountedEl() {
  return getCurrentInstance().appContext.app._container;
}

export function replaceBreaks(text) {
  if (!text) return;
  return text.replace(/\\n/g, "<br>");
}

export function addSecondsInDate(date, seconds) {
  return date.setSeconds(date.getSeconds() + seconds);
}

export function durationToWords(duration) {
  return moment.preciseDiff(new Date(), addSecondsInDate(new Date(), duration));
}

export function roundOffInteger(number, differences) {
  const roundOffNum = differences * 2;
  return Math.round(number / roundOffNum) * roundOffNum;
}

export function trimSlash(url) {
  return url.replace(/^\/*/, "").replace(/\/*$/, "");
}

export function pascalCase(str) {
  str = camelCase(str);
  return str.charAt(0).toUpperCase() + str.substring(1);
}